import { Box, Flex } from '../../../shared/layouts';
import DisplayLine from '../../../shared/DisplayLine';
import { findTranslation } from '../../../../utils/helpers';
import { HiddenInput } from '../../../shared/RadioOption';
import Errors from '../../../shared/Errors';
import { RadioProps } from '../../../shared/RadioButton';
import styled from '@emotion/styled';
import {FC} from "react";

export interface RadioBlocksProps extends RadioProps {
  showSummarized?: boolean;
  showInline?: boolean;
  switchToSpecific?: (value: string) => void;
}

interface StyledProps {
  as?: string;
  checked?: string;
  external?: boolean;
}

export const Container = styled(Box)<StyledProps>({
  display: 'flex',
  position: 'relative',
  cursor: 'pointer',
  userSelect: 'none',
  flex: '0 0 100px'
});

const Radio = styled.span<StyledProps>(({ theme, checked, external }) => {
  const backgroundColor = theme.colors?.white || 'white';
  return {
    flex: '1 0 50px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
    minHeight: '48px',
    maxWidth: '120px',
    padding: `${theme?.space?.s}px ${theme?.space?.l}px`,
    backgroundColor: checked
      ? theme?.colors?.primary
      : external
      ? theme.colors.primaryBackground
      : backgroundColor,
    borderRadius: '16px',
    border: '2px solid',
    borderColor: external
      ? theme.colors?.primaryBackground
      : theme.colors?.primary,
    color: checked || external ? backgroundColor : theme.colors?.primary,
    fontFamily: theme?.font,
    fontWeight: 'bold',
    lineHeight: '1rem'
  };
});

const RadioButtonBlocks: FC<RadioBlocksProps> = ({
  value,
  onChange,
  name,
  options,
  autoFocus,
  direction = 'row',
  readOnly,
  label,
  errors,
  showSummarized,
  showInline,
  switchToSpecific
}) => {
  const optionList = showSummarized ? options.slice(0, 5) : options;
  const hiddenAmount =
    options > optionList ? options?.length - optionList?.length : null;

  if (readOnly) {
    return (
      <DisplayLine label={label} value={findTranslation(value, options)} />
    );
  }

  const input = (
    <Box
      maxWidth={{ mobile: '80vw', desktop: '90vw' }}
      css={{ overflowX: 'scroll', paddingBottom: showInline ? '16px' : null }}
    >
      <Flex
        width="100%"
        flexDirection={direction}
        flexWrap={showInline ? null : 'wrap'}
        justifyContent={'flex-start'}
        css={theme => ({
          gap: theme?.space?.m
        })}
      >
        {optionList.map((option, i) => {
          const optionValue =
            typeof option === 'object' ? option?.value : option;
          const optionLabel =
            typeof option === 'object' ? option?.label : option;

          return (
            <RadioBlock
              key={optionValue.toString()}
              autoFocus={autoFocus && (value === optionValue || i === 0)}
              name={name}
              value={optionValue}
              label={optionLabel}
              onChange={onChange}
              checked={value === optionValue}
            />
          );
        })}
        {showSummarized && hiddenAmount && (
          <RadioBlock
            autoFocus={false}
            name={'showMore'}
            value={name}
            label={`+${hiddenAmount}`}
            checked={false}
            external
            onChange={() => {
              switchToSpecific(name);
            }}
          />
        )}
      </Flex>
    </Box>
  );

  return (
    <Box>
      <Box
        fontWeight={'bold'}
        mb={'s'}
        css={{ ':first-letter': { textTransform: 'uppercase' } }}
      >
        {label}
      </Box>
      {options.length > 0 ? input : 'Geen mogelijkheden beschikbaar'}
      {errors && <Errors errors={errors} />}
    </Box>
  );
};

const RadioBlock = ({
  value,
  onChange,
  name,
  label,
  checked,
  autoFocus,
  external = false
}) => (
  <Container as={'label'}>
    <HiddenInput
      autoFocus={autoFocus}
      name={name}
      value={value}
      type="radio"
      onChange={e => {
        let newValue: string | boolean = e.target.value;
        if (newValue === 'true') newValue = true;
        if (newValue === 'false') newValue = false;
        onChange(newValue);
      }}
      checked={checked}
    />
    <Radio checked={checked} external={external}>
      {label}
    </Radio>
  </Container>
);

export default RadioButtonBlocks;
